import { useState, useEffect, useCallback } from 'react';
import { utils, read } from 'xlsx';
import {
  useGetRealStateInsuranceData,
  useImportRealStateInsuranceData,
} from '../../../hooks/useContracts';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { downloadExcel } from '../utils/realStateTaxesUtils';
import { ITEMS_PER_PAGE } from '../constants/realStateTaxesConstants';

export const useRealStateTaxes = () => {
  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();

  // Estados para la tabla
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    plan: '',
    group: '',
  });
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  // Estados para la importación
  const [selectedFile, setSelectedFile] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const realStateTaxesMutation = useGetRealStateInsuranceData();
  const importMutation = useImportRealStateInsuranceData();

  // Efecto para setear el primer plan cuando se cargan los datos
  useEffect(() => {
    if (plans.length > 0 && !filter.plan) {
      setFilter((prev) => ({
        ...prev,
        plan: plans[0].uuid,
      }));
    }
  }, [plans, filter.plan]);

  // Función para cargar datos de la tabla
  const fetchData = useCallback(async () => {
    try {
      const filters = {
        page,
        per_page: rowsPerPage,
        ...(filter.plan && { plan: filter.plan }),
        ...(filter.group && { group: filter.group }),
      };

      const response = await realStateTaxesMutation.mutateAsync(filters);
      setData(response.data || []);
      setTotalPages(response.pages || 0);
      setTotalCount(response.total || 0);
      if (response.pages && page > response.pages) {
        setPage(Math.max(1, response.pages));
      }
    } catch (error) {
      console.error('Error fetching real state taxes data:', error);
      setData([]);
      setTotalPages(0);
      setTotalCount(0);
    }
  }, [page, rowsPerPage, filter.plan, filter.group, realStateTaxesMutation]);

  // Cargar datos cuando cambien los filtros
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filter.plan, filter.group]);  //! NO MODIFICAR ESTO SINO TENGO RENDER INFINITO

  // Funciones para la tabla
  const handleFilter = useCallback((event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  }, []);

  const handleDownloadExcel = useCallback(async () => {
    try {
      const exportFilters = {
        page: 1,
        per_page: 1000,
        ...(filter.plan && { plan: filter.plan }),
        ...(filter.group && { group: filter.group }),
      };

      const response = await realStateTaxesMutation.mutateAsync(exportFilters);
      downloadExcel(response.data || []);
    } catch (error) {
      console.error('Error downloading excel:', error);
    }
  }, [filter.plan, filter.group, realStateTaxesMutation]);

  // Funciones para la importación
  const handleFileUpload = useCallback((event) => {
    setSelectedFile(event.target.files[0]);
  }, []);

  const readExcel = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = read(data, { type: 'array' });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      let json = utils.sheet_to_json(worksheet, { header: 1 });
      json = json.map((row) => [row[2], row[0], row[1], row[5]]);
      setTableData(json);
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleImportValues = useCallback(() => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: 'Es necesario seleccionar un archivo',
        severity: 'error',
      });
      return;
    }

    readExcel(selectedFile);
    setImportDialogOpen(true);
  }, [selectedFile, readExcel]);

  const handleCloseImportDialog = useCallback(() => {
    setImportDialogOpen(false);
    setTableData([]);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const response = await importMutation.mutateAsync(selectedFile);

      setSnackbar({
        open: true,
        message: `Importación completada: ${response.statistics.updated} contratos actualizados`,
        severity: 'success',
      });

      // Limpiar estados
      setSelectedFile(null);
      setTableData([]);
      setImportDialogOpen(false);

      // Refrescar datos
      await fetchData();
    } catch (error) {
      console.error('Error al importar datos:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error al importar datos',
        severity: 'error',
      });
    }
  }, [selectedFile, importMutation, fetchData]);

  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    // Data de la tabla
    data,
    isLoading: realStateTaxesMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,
    plans,
    groups,

    // Funciones de tabla
    handleFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadExcel,

    // Estados de importación
    selectedFile,
    tableData,
    importDialogOpen,
    importLoading: importMutation.isPending,
    snackbar,

    // Funciones de importación
    handleFileUpload,
    handleImportValues,
    handleCloseImportDialog,
    handleUpload,
    handleCloseSnackbar,
  };
};
